/**
 * Base di conoscenza del Support Hub (Task 29): schede dei casi gia risolti,
 * in bozza finche un operatore non le approva. Due adapter con la stessa
 * interfaccia: admin (`axiosInstance`, baseURL con `/api` gia inclusa) e
 * rappresentante (`repAxios`). Singleton di modulo, come gli adapter ticket.
 */
import axiosInstance from './axiosInstance';
import { repAxios } from './representativeService';

export type KnowledgeStatus = 'draft' | 'approved' | 'archived';

export interface KnowledgeActorRef {
  actorType: 'user' | 'representative' | 'system';
  actorId?: string | null;
  nome?: string;
}

export interface KnowledgeCard {
  _id: string;
  title: string;
  question: string;
  answer: string;
  tags: string[];
  groupId: string | null;
  groupName: string | null;
  sourceTicketId: string | null;
  sourceTicketNumber: string | null;
  /** L'operatore puo aprire il ticket di origine. */
  sourceTicketAccessible?: boolean;
  status: KnowledgeStatus;
  createdBy: KnowledgeActorRef | null;
  approvedBy: KnowledgeActorRef | null;
  approvedAt: string | null;
  updatedBy: KnowledgeActorRef | null;
  /** Ha l'embedding: trovata anche per significato, non solo per parole. */
  embedded: boolean;
  usageCount: number;
  lastUsedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface KnowledgeListParams {
  status: KnowledgeStatus;
  groupId?: string;
  q?: string;
  page?: number;
  limit?: number;
}

export interface KnowledgeListResult {
  items: KnowledgeCard[];
  total: number;
  page: number;
  totalPages: number;
  counts: Record<KnowledgeStatus, number>;
}

export interface KnowledgeFields {
  title: string;
  question: string;
  answer: string;
  tags: string[];
  groupId?: string;
}

/** Esito di una scrittura: la scheda aggiornata e un eventuale avviso (embedding). */
export interface KnowledgeWriteResult {
  card: KnowledgeCard;
  warning?: string;
  created?: boolean;
}

export interface KnowledgeApi {
  kind: 'admin' | 'representative';
  /** Solo admin: eliminazione definitiva. */
  canDelete: boolean;
  list(params: KnowledgeListParams): Promise<KnowledgeListResult>;
  get(id: string): Promise<KnowledgeCard>;
  groups(): Promise<{ _id: string; nome: string }[]>;
  update(id: string, fields: Partial<KnowledgeFields>): Promise<KnowledgeWriteResult>;
  approve(id: string): Promise<KnowledgeWriteResult>;
  archive(id: string): Promise<KnowledgeWriteResult>;
  restore(id: string): Promise<KnowledgeWriteResult>;
  create(fields: KnowledgeFields & { groupId: string }, approve?: boolean): Promise<KnowledgeWriteResult>;
  generateFromTicket(ticketId: string): Promise<KnowledgeWriteResult>;
  remove?(id: string): Promise<void>;
  /** Percorso del ticket di origine nell'area corrente. */
  ticketPath(ticketId: string): string;
  /** Percorso della pagina con la scheda aperta. */
  cardPath(cardId: string): string;
}

type Http = { get: typeof axiosInstance.get; post: typeof axiosInstance.post; patch: typeof axiosInstance.patch; delete: typeof axiosInstance.delete };

const build = (
  http: Http,
  base: string,
  kind: KnowledgeApi['kind'],
  paths: { ticket: (id: string) => string; card: (id: string) => string }
): KnowledgeApi => ({
  kind,
  canDelete: kind === 'admin',
  list: async (params) => (await http.get(base, { params })).data.data,
  get: async (id) => (await http.get(`${base}/${id}`)).data.data,
  groups: async () => (await http.get(`${base}/groups`)).data.data,
  update: async (id, fields) => (await http.patch(`${base}/${id}`, fields)).data.data,
  approve: async (id) => (await http.post(`${base}/${id}/approve`)).data.data,
  archive: async (id) => (await http.post(`${base}/${id}/archive`)).data.data,
  restore: async (id) => (await http.post(`${base}/${id}/restore`)).data.data,
  create: async (fields, approve) => (await http.post(base, approve ? { ...fields, status: 'approved' } : fields)).data.data,
  generateFromTicket: async (ticketId) => (await http.post(`${base}/generate/${ticketId}`)).data.data,
  ...(kind === 'admin'
    ? { remove: async (id: string) => { await http.delete(`${base}/${id}`); } }
    : {}),
  ticketPath: paths.ticket,
  cardPath: paths.card,
});

export const adminKnowledgeApi: KnowledgeApi = build(axiosInstance, '/admin/support-knowledge', 'admin', {
  ticket: (id) => `/admin/centro-ticket?ticket=${id}`,
  card: (id) => `/admin/centro-ticket/conoscenza?scheda=${id}`,
});

export const representativeKnowledgeApi: KnowledgeApi = build(repAxios, '/representative/support/knowledge', 'representative', {
  ticket: (id) => `/area-associativa/supporto/${id}`,
  card: (id) => `/area-associativa/supporto/conoscenza?scheda=${id}`,
});
